import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  CircularProgress,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAppConfigRequest,
  getBullionRatesRequest,
  getCarouselRequest,
  getHoldingsRequest,
  getSipBalanceRequest,
  getKycDetailsRequest,
} from "../../redux/Dashboard/dashboardSlice";
import { createSellRequest, resetSellState } from "../../redux/Sell/sellSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CarouselSection from "../CarouselSection/CarouselSlider";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";

const SellScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sellCreated } = useSelector((state) => state.sell);

  const { data, bullionRates, appConfig, carousel } = useSelector(
    (state) => state.dashboard || {},
  );
  const decimal = appConfig?.decimalPrecision ?? 4;
  const enableSellViaUPI = appConfig?.enableSellViaUPI ?? true;
  const { data: holdings } = useSelector((state) => state.dashboard || {});
  const { kycDetails } = useSelector((state) => state.dashboard || {});
  const bankDetails = kycDetails?.bank_details || {};

  const [selectedMetal, setSelectedMetal] = useState(null);
  const [grams, setGrams] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bank");
  const [upiId, setUpiId] = useState("");

  const [topCarousel, setTopCarousel] = React.useState(null);
  const [midCarousel, setMidCarousel] = React.useState(null);
  const [bottomCarousel, setBottomCarousel] = React.useState(null);

  useEffect(() => {
    dispatch(
      getHoldingsRequest({ jeweller: `${process.env.REACT_APP_JEWELLER_ID}` }),
    );
    dispatch(
      getBullionRatesRequest({ id: `${process.env.REACT_APP_JEWELLER_ID}` }),
    );
    dispatch(getAppConfigRequest());
    dispatch(getCarouselRequest());
    dispatch(getSipBalanceRequest());
    dispatch(getKycDetailsRequest());
  }, [dispatch]);

  const handleClick = () => {
    navigate("/Profile", { state: { tab: "bank" } });
  };

  const filteredMetals = useMemo(() => {
    if (!bullionRates) return [];
    return bullionRates
      .filter((r) => r.bullion?.metalName?.toLowerCase() !== "money")
      .map((r) => r.bullion.metalName);
  }, [bullionRates]);

  useEffect(() => {
    if (!selectedMetal && filteredMetals.length > 0) {
      setSelectedMetal(filteredMetals[0]);
    }
  }, [filteredMetals, selectedMetal]);

  useEffect(() => {
    if (carousel?.data?.length > 0) {
      let top = {},
        mid = {},
        bottom = {};

      carousel.data.forEach((e) => {
        switch (e.name) {
          case "sell_main_top":
            top = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
          case "sell_main_middle":
            mid = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
          case "sell_main_bottom":
            bottom = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
          default:
            break;
        }
      });

      setTopCarousel(top);
      setMidCarousel(mid);
      setBottomCarousel(bottom);
    } else {
      setTopCarousel(null);
      setMidCarousel(null);
      setBottomCarousel(null);
    }
  }, [carousel]);

  const selectedRate = useMemo(() => {
    if (!selectedMetal || !bullionRates) return null;
    return bullionRates.find(
      (rate) =>
        rate.bullion.metalName === selectedMetal &&
        rate.rateType?.toLowerCase() === "sell",
    );
  }, [selectedMetal, bullionRates]);

  const currentSellPrice =
    selectedRate?.ratePerGram ||
    selectedRate?.currentSellRate ||
    selectedRate?.sellRate ||
    0;
  const convenienceFeeRate = 0.05;

  const currentHolding = useMemo(() => {
    if (!holdings || !selectedMetal) return null;
    const metalHoldings = holdings.filter((h) => h.metalName === selectedMetal);
    if (metalHoldings.length === 0) return null;

    const totalAvailable = metalHoldings.reduce(
      (sum, h) => sum + (Number(h.availableBalance) || 0),
      0,
    );
    const totalGift = metalHoldings.reduce(
      (sum, h) => sum + (Number(h.giftBalance) || 0),
      0,
    );

    const netGrams = totalAvailable - totalGift;
    const totalValue = netGrams * currentSellPrice;

    return {
      grams: netGrams.toFixed(decimal),
      amount: totalValue.toFixed(decimal),
    };
  }, [holdings, selectedMetal, currentSellPrice]);

  const maxGrams = Number(currentHolding?.grams || 0);
  const maxAmount = Number(currentHolding?.amount || 0);
  const handleGramsChange = (e) => {
    const g = e.target.value;
    const gramDecimalRegex = new RegExp(`^\\d*(\\.\\d{0,${decimal}})?$`);
    if (g !== "" && !gramDecimalRegex.test(g)) return;
    if (Number(g) > maxGrams) {
      toast.error("Entered grams is greater than available balance", {
        toastId: "sell-grams-limit",
      });
      return;
    }
    setGrams(g);
    if (g && !isNaN(g)) {
      const value = (g * currentSellPrice).toFixed(decimal);
      if (Number(value) > maxAmount) {
        toast.error("Entered amount exceeds available balance");
        return;
      }
      setAmount(value);
    } else {
      setAmount("");
    }
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    if (val !== "" && (!/^\d*\.?\d*$/.test(val) || Number(val) < 0)) return;
    if (Number(val) > maxAmount) {
      toast.error("Entered amount exceeds available balance");
      return;
    }
    setAmount(val);
    if (val && !isNaN(val)) {
      const g = (val / currentSellPrice).toFixed(decimal);
      if (Number(g) > maxGrams) {
        toast.error("Entered grams is greater than available balance");
        return;
      }
      setGrams(g);
    } else {
      setGrams("");
    }
  };

  const convenienceFee = amount
    ? (amount * convenienceFeeRate).toFixed(decimal)
    : 0;
  const finalAmount = amount ? (amount - convenienceFee).toFixed(decimal) : 0;

  const createSellOrder = () => {
    if (!grams) {
      toast.error("Please enter valid grams");
      return;
    }
    if (Number(grams) <= 0) {
      toast.error("Invalid weight entered");
      return;
    }

    if (paymentMethod === "bank" && !bankDetails?._id) {
      toast.error("No bank details found. Please add a bank first.");
      return;
    }

    if (paymentMethod === "upi" && !upiId) {
      toast.error("Please enter UPI ID");
      return;
    }

    const purity = selectedRate?.bullion?.purity || 999;
    const payload = {
      weight: parseFloat(grams),
      metalName: selectedMetal,
      orderType: "Sell",
      purity: purity,
      bankid: paymentMethod === "bank" ? bankDetails._id : null,
      upiid: paymentMethod === "upi" ? upiId : null,
    };

    console.log("Sell Payload:", payload);

    dispatch(createSellRequest(payload));
  };

  useEffect(() => {
    if (sellCreated) {
      navigate("/SellHistory");
      dispatch(resetSellState());
    }
  }, [sellCreated, navigate, dispatch]);

  const themeColors = useAppTheme();

  const { symbol } = useCurrency();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 4,
        background: themeColors.scaffoldGradientColors,
      }}
    >
      <Card
        sx={{
          maxWidth: 1000,
          width: "100%",
          borderRadius: 3,
          boxShadow: 8,
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
        }}
      >
        <CardContent
          sx={{
            p: { xs: 3, sm: 5 },
            color: themeColors.text,
          }}
        >
          {/* Page Title */}
          <Typography
            variant="h4"
            fontWeight="bold"
            align="center"
            sx={{
              mb: 2,
              fontSize: { xs: "1.8rem", sm: "2.2rem" },
              color: themeColors.primary,
            }}
          >
            Sell Metal
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {topCarousel && (
            <CarouselSection
              slides={topCarousel.slides}
              height={topCarousel.height}
              showNavigator={topCarousel.showNavigator}
              duration={topCarousel.duration}
              enabled={topCarousel.enabled}
            />
          )}

          {/* Metal Selection */}
          {/* Metal Selection – Pill UI */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                background: "#0B2C4A",
                borderRadius: "24px",
                padding: "6px",
                gap: "6px",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              {[...new Set(bullionRates?.map((r) => r.bullion?.metalName))].map(
                (name, index) => {
                  const metal = bullionRates.find(
                    (r) => r.bullion?.metalName === name,
                  );
                  const karat = metal?.bullion?.karat || "";
                  const purity = metal?.bullion?.purity || "";

                  const isSelected = selectedMetal === name;

                  return (
                    <Box
                      key={index}
                      onClick={() => setSelectedMetal(name)}
                      sx={{
                        px: { xs: 1.5, sm: 3 },
                        py: 1.2,
                        borderRadius: "30px",
                        cursor: "pointer",
                        minWidth: { xs: "46%", sm: 110 },
                        flex: { xs: "1 1 46%", sm: "0 0 auto" },
                        textAlign: "center",
                        transition: "all 0.3s ease",
                        background: isSelected
                          ? themeColors.primary
                          : "transparent",
                        border: isSelected
                          ? "none"
                          : `1px solid ${themeColors.scaffold}`,
                        boxShadow: isSelected ? themeColors.border : "none",
                        transition: "all 0.25s ease",
                        border: `1px solid ${themeColors.scaffold}`,
                        color: themeColors.text,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          lineHeight: 1.2,
                        }}
                      >
                        {name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          opacity: 0.8,
                        }}
                      >
                        {karat}k – {purity}
                      </Typography>
                    </Box>
                  );
                },
              )}
            </Box>
          </Box>
          {/* Selected Metal Display */}
          <Box
            sx={{
              mb: 3,
              px: 2,
            }}
          >
            <Box
              sx={{
                height: 60,
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                color: "#000",
                background:
                  selectedMetal?.toLowerCase() === "gold"
                    ? "linear-gradient(135deg, #FFD700, #DAA520)"
                    : "linear-gradient(135deg, #C0C0C0, #E0E0E0)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    letterSpacing: 1,
                  }}
                >
                  {selectedMetal?.toUpperCase()}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    opacity: 0.9,
                  }}
                >
                  {(() => {
                    const metal = bullionRates?.find(
                      (r) => r.bullion?.metalName === selectedMetal,
                    );
                    return `${metal?.bullion?.karat || ""}k – ${
                      metal?.bullion?.purity || ""
                    }`;
                  })()}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Current Balance */}
          {currentHolding ? (
            <Card
              variant="outlined"
              sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
                background: themeColors.bigContainer,
                border: `1px solid ${themeColors.scaffold}`,
                color: themeColors.text,
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  mb={1}
                  align="center"
                >
                  Current Balance
                </Typography>
                <Grid container spacing={2} justifyContent="center">
                  <Grid size={{ xs: 6 }} textAlign="center">
                    <Typography variant="body2">Grams</Typography>
                    <Typography variant="h6">{currentHolding.grams}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }} textAlign="center">
                    <Typography variant="body2">Value ({symbol})</Typography>
                    <Typography variant="h6">
                      {currentHolding.amount}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ) : (
            <Box textAlign="center" mb={3}>
              <CircularProgress size={28} sx={{ color: "#9E3ED7" }} />
              <Typography variant="body2" mt={1}>
                Loading current balance...
              </Typography>
            </Box>
          )}

          {midCarousel && (
            <CarouselSection
              slides={midCarousel.slides}
              height={midCarousel.height}
              showNavigator={midCarousel.showNavigator}
              duration={midCarousel.duration}
              enabled={midCarousel.enabled}
            />
          )}

          <Card
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
              background: themeColors.bigContainer,
              border: `1px solid ${themeColors.scaffold}`,
              color: themeColors.text,
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                mb={2}
                align="center"
              >
                Sell {selectedMetal}
              </Typography>

              <Grid container spacing={2}>
                <Grid size={5}>
                  <TextField
                    fullWidth
                    label="Enter Grams"
                    value={grams}
                    onChange={handleGramsChange}
                    type="number"
                    inputProps={{ min: 0 }}
                    size="medium"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: themeColors.mediumContainer,
                        color: themeColors.text,
                      },
                      "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                        {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                      "& .MuiInputLabel-root": {
                        color: themeColors.text,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: themeColors.scaffold,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: themeColors.primary,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: themeColors.primary,
                      },
                    }}
                  />
                </Grid>
                <Grid
                  size={2}
                  textAlign="center"
                  sx={{ py: 1, color: themeColors.text }}
                >
                  <SwapHorizIcon fontSize="large" />
                </Grid>
                <Grid size={5}>
                  <TextField
                    fullWidth
                    label="Amount"
                    value={amount}
                    onChange={handleAmountChange}
                    type="number"
                    inputProps={{ min: 0 }}
                    size="medium"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: themeColors.mediumContainer,
                        color: themeColors.text,
                      },
                      "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                        {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                      "& .MuiInputLabel-root": {
                        color: themeColors.text,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: themeColors.scaffold,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: themeColors.primary,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: themeColors.primary,
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Box mt={3} textAlign="center">
                <Typography variant="body2">
                  Convenience Fee (5.0%): {symbol} {convenienceFee}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  Final Amount: {symbol} {finalAmount}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Current Sell Price */}
          <Typography
            variant="body1"
            mb={2}
            sx={{ fontWeight: 500, textAlign: "center" }}
          >
            Current Sell Price:{" "}
            <strong>
              {symbol} {currentSellPrice ? currentSellPrice.toFixed(decimal) : 0}/gm
            </strong>
          </Typography>

          {/* Payment Method (Now in Row Layout) */}
          <Card
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
            }}
          >
            <CardContent
              sx={{
                background: themeColors.bigContainer,
                border: `1px solid ${themeColors.scaffold}`,
                color: themeColors.text,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                Select Receiving Method
              </Typography>

              <Grid container spacing={7}>
                {/* Bank Option */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      p: 2,
                      border:
                        paymentMethod === "bank"
                          ? `1px solid ${themeColors.border}`
                          : "1px solid #ddd",
                      borderRadius: 2,
                      cursor: "pointer",
                      "&:hover": { borderColor: themeColors.border },
                    }}
                    onClick={() => setPaymentMethod("bank")}
                  >
                    <FormControlLabel
                      value="bank"
                      control={<Radio checked={paymentMethod === "bank"} />}
                      label="Bank Account"
                    />
                    {paymentMethod === "bank" && (
                      <Box ml={8} mb={2}>
                        {bankDetails?.bankName ? (
                          <>
                            <Typography variant="body2">
                              Account Number:{" "}
                              {bankDetails?.accNumber?.replace(
                                /.(?=.{4})/g,
                                "X",
                              )}
                            </Typography>
                            <Typography variant="body2" mb={1}>
                              Bank:{bankDetails?.bankName}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No bank details found.
                          </Typography>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            px: 2,
                          }}
                          onClick={handleClick}
                        >
                          {bankDetails?.bankName
                            ? "Edit Bank Details"
                            : "Add Bank Details"}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* UPI Option */}
                {enableSellViaUPI && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{
                        p: 2,
                        border:
                          paymentMethod === "upi"
                            ? `2px solid ${themeColors.border}`
                            : "1px solid #ddd",
                        borderRadius: 2,
                        cursor: "pointer",
                        "&:hover": { borderColor: themeColors.border },
                      }}
                      onClick={() => setPaymentMethod("upi")}
                    >
                      <FormControlLabel
                        value="upi"
                        control={<Radio checked={paymentMethod === "upi"} />}
                        label="UPI"
                      />
                      {paymentMethod === "upi" && (
                        <Box ml={4} mt={1}>
                          <TextField
                            label="Enter UPI ID"
                            size="small"
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                background: themeColors.mediumContainer,
                                color: themeColors.text,
                              },
                              "& .MuiInputLabel-root": {
                                color: themeColors.text,
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: themeColors.scaffold,
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: themeColors.primary,
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: themeColors.primary,
                                },
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {bottomCarousel && (
            <CarouselSection
              slides={bottomCarousel.slides}
              height={bottomCarousel.height}
              showNavigator={bottomCarousel.showNavigator}
              duration={bottomCarousel.duration}
              enabled={bottomCarousel.enabled}
            />
          )}

          {/* Action Buttons */}
          <Box display="flex" justifyContent="center" gap={2}>
            <Button
              variant="contained"
              onClick={createSellOrder}
              sx={{
                background: themeColors.primary,
                color: themeColors.text,
                "&:hover": {
                  background: themeColors.svgColor,
                },
                border: `1px solid ${themeColors.scaffold}`,
                fontWeight: 500,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                px: 5,
                py: 1.2,
                textTransform: "none",
                borderRadius: 2,
              }}
            >
              Sell
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/SellHistory")}
              sx={{
                background: themeColors.primary,
                color: themeColors.text,
                "&:hover": {
                  background: themeColors.svgColor,
                },
                border: `1px solid ${themeColors.scaffold}`,
                fontWeight: 500,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                px: 5,
                py: 1.2,
                textTransform: "none",
                borderRadius: 2,
              }}
            >
              History
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SellScreen;
